import React, { useEffect, useMemo, useState } from 'react';
import { useAuth } from '@clerk/clerk-react';
import { colors } from '../theme';
import { loadCatalog, generateSuggestions } from './nameSuggestions';

const API_BASE = process.env.REACT_APP_API_URL || '';

export default function NameSuggest({ tenant, uid, initialName, onSaved }) {
  const { getToken } = useAuth();
  const [catalog, setCatalog] = useState(null);
  const [catalogError, setCatalogError] = useState(null);
  const [value, setValue] = useState(initialName || '');
  const [saving, setSaving] = useState(false);
  const [message, setMessage] = useState('');
  const [rotation, setRotation] = useState(0);

  useEffect(() => {
    let cancelled = false;
    loadCatalog()
      .then(result => {
        if (!cancelled) {
          setCatalog(result);
        }
      })
      .catch(err => {
        console.error('Failed to load name catalog', err);
        if (!cancelled) {
          setCatalogError('Unable to load name suggestions right now.');
        }
      });
    return () => {
      cancelled = true;
    };
  }, []);

  useEffect(() => {
    setValue(initialName || '');
  }, [initialName]);

  const suggestions = useMemo(() => {
    if (!catalog) return [];
    return generateSuggestions(uid, catalog, rotation, 5);
  }, [catalog, uid, rotation]);

  async function handleSave(event) {
    event.preventDefault();
    setSaving(true);
    setMessage('');

    try {
      const token = await getToken();
      if (!token) {
        throw new Error('Missing auth token');
      }

      const response = await fetch(
        `${API_BASE}/farm/${tenant}/uids/${encodeURIComponent(uid)}/name`,
        {
          method: 'PATCH',
          headers: {
            'Content-Type': 'application/json',
            Authorization: `Bearer ${token}`,
          },
          body: JSON.stringify({ customName: value }),
        }
      );

      if (!response.ok) {
        const body = await response.json().catch(() => ({}));
        throw new Error(body?.error || `Request failed (${response.status})`);
      }

      const body = await response.json();
      const nextName = body.customName || '';
      setValue(nextName);
      setMessage(nextName ? 'Name saved' : 'Name cleared');
      if (typeof onSaved === 'function') {
        onSaved(nextName);
      }
    } catch (err) {
      console.error('Failed to save custom name', err);
      setMessage(err.message || 'Failed to save name');
    } finally {
      setSaving(false);
    }
  }

  function applySuggestion(text) {
    setValue(text);
    setMessage('');
  }

  return (
    <form onSubmit={handleSave} style={containerStyle}>
      <label style={labelStyle}>
        Custom name
        <input
          type="text"
          value={value}
          onChange={event => {
            setValue(event.target.value);
            setMessage('');
          }}
          placeholder="Add a friendly name"
          style={inputStyle}
          maxLength={192}
        />
      </label>
      <div style={actionsRowStyle}>
        <button type="submit" style={{ ...saveButtonStyle, opacity: saving ? 0.6 : 1 }} disabled={saving}>
          {saving ? 'Saving…' : 'Save'}
        </button>
        <button
          type="button"
          onClick={() => {
            setRotation(rotation + 1);
            setMessage('');
          }}
          style={shuffleButtonStyle}
        >
          Shuffle
        </button>
        {message && <span style={messageStyle}>{message}</span>}
      </div>

      {catalogError && <p style={errorStyle}>{catalogError}</p>}

      {suggestions.length > 0 && (
        <div style={suggestionsStyle}>
          {suggestions.map(name => (
            <button
              type="button"
              key={name}
              onClick={() => applySuggestion(name)}
              style={suggestionButtonStyle}
            >
              {name}
            </button>
          ))}
        </div>
      )}
    </form>
  );
}

const containerStyle = {
  display: 'flex',
  flexDirection: 'column',
  gap: '0.75rem',
};

const labelStyle = {
  display: 'flex',
  flexDirection: 'column',
  fontSize: '0.85rem',
  color: colors.muted,
  gap: '0.35rem',
};

const inputStyle = {
  padding: '0.6rem 0.75rem',
  borderRadius: '10px',
  border: `1px solid ${colors.border}`,
  fontSize: '1rem',
};

const actionsRowStyle = {
  display: 'flex',
  alignItems: 'center',
  gap: '0.75rem',
  flexWrap: 'wrap',
};

const saveButtonStyle = {
  backgroundColor: colors.primary,
  color: colors.background,
  border: 'none',
  borderRadius: '999px',
  padding: '0.45rem 1.2rem',
  fontWeight: 600,
  cursor: 'pointer',
};

const shuffleButtonStyle = {
  backgroundColor: 'transparent',
  borderRadius: '999px',
  border: `1px solid ${colors.primary}`,
  color: colors.primary,
  padding: '0.45rem 1.2rem',
  fontWeight: 600,
  cursor: 'pointer',
};

const messageStyle = {
  fontSize: '0.85rem',
  color: colors.muted,
};

const errorStyle = {
  color: colors.danger,
  margin: 0,
  fontSize: '0.85rem',
};

const suggestionsStyle = {
  display: 'flex',
  gap: '0.5rem',
  flexWrap: 'wrap',
};

const suggestionButtonStyle = {
  borderRadius: '999px',
  border: '1px solid rgba(70,187,137,0.4)',
  padding: '0.35rem 0.8rem',
  backgroundColor: 'rgba(70,187,137,0.12)',
  color: colors.text,
  cursor: 'pointer',
  fontSize: '0.9rem',
};
